import apiClient from "@/api/client";
import type {
  DraftHistoryEntry,
  DraftOverviewRow,
  Timesheet,
  TimesheetLockStatus,
  TimesheetMissingRow,
  TimesheetStatus,
} from "../types";

export interface SecondaryContractBrief {
  id: string;
  kind: string;
  kind_display: string;
  effective_from: string;
  effective_to: string | null;
  /** "hourly" = zł/h, "monthly" = flat zł/miesiąc */
  rate_type: "hourly" | "monthly";
  hourly_gross_rate: string;
  company_short_code: string | null;
  company_name: string | null;
  /** True iff the contract is in effect during the requested year/month. */
  is_active_in_period: boolean;
}

export interface PrimaryContractBrief {
  contract_type: "uop" | "uz" | "b2b";
  employment_fraction_numerator: number;
  employment_fraction_denominator: number;
  company_short_code: string | null;
  company_name: string | null;
}

export interface ExistingTimesheet {
  id: string;
  /** null = primary (UoP/UZ baseline); otherwise the secondary contract UUID. */
  secondary_contract: string | null;
  /** Withdrawn cards are excluded server-side — a slot mid-replacement has
   *  TWO entries: the old card (withdrawal_requested) and the new (pending). */
  status: TimesheetStatus;
}

export interface MySecondaryContractsResponse {
  secondaries: SecondaryContractBrief[];
  primary: PrimaryContractBrief;
  /** Timesheets already submitted by current user for the requested year+month. */
  existing: ExistingTimesheet[];
}

export interface ProxyTarget {
  id: string;
  full_name: string;
  role: string;
  contract_type: string | null;
  /** False for recently-deactivated employees still within the proxy grace period. */
  is_active: boolean;
}

export interface PdfPreviewDay {
  day: number;
  date_str: string;
  weekday_short: string;
  is_weekend: boolean;
  is_holiday: boolean;
  leave_label: string;
  other_leave_label: string;
  sick_leave_label: string;
  auto_note: string;
  is_working: boolean;
}

export interface PdfPreviewResponse {
  days: PdfPreviewDay[];
  is_uz: boolean;
  employee_name: string;
  year: number;
  month: number;
  secondary_contract: {
    id: string;
    kind: string;
    effective_from: string;
    effective_to: string | null;
    rate_type: "hourly" | "monthly";
    hourly_gross_rate: string;
  } | null;
}

export const timesheetsApi = {
  list: (params?: Record<string, string>) =>
    apiClient
      .get<{ data: Timesheet[]; meta: { count: number; page: number; page_size: number } }>(
        "/timesheets/",
        { params: { page_size: "200", ...params } },
      )
      .then((r) => r.data.data),

  get: (id: string) =>
    apiClient
      .get<{ data: Timesheet }>(`/timesheets/${id}/`)
      .then((r) => r.data.data),

  create: (formData: FormData) =>
    apiClient
      .post<{ data: Timesheet }>("/timesheets/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data.data),

  approve: (id: string, comment?: string) =>
    apiClient
      .post<{ data: Timesheet }>(`/timesheets/${id}/approve/`, { comment: comment ?? "" })
      .then((r) => r.data.data),

  reject: (id: string, comment: string) =>
    apiClient
      .post<{ data: Timesheet }>(`/timesheets/${id}/reject/`, { comment })
      .then((r) => r.data.data),

  /** Director: accept the withdrawal of an approved card (→ "withdrawn").
   *  The replacement card stays pending and needs its own approval. */
  approveWithdrawal: (id: string, comment?: string) =>
    apiClient
      .post<{ data: Timesheet }>(`/timesheets/${id}/approve-withdrawal/`, { comment: comment ?? "" })
      .then((r) => r.data.data),

  /** Director: refuse the withdrawal — the card returns to "approved" and the
   *  pending replacement is rejected with the same comment. */
  rejectWithdrawal: (id: string, comment: string) =>
    apiClient
      .post<{ data: Timesheet }>(`/timesheets/${id}/reject-withdrawal/`, { comment })
      .then((r) => r.data.data),

  /** CEO only: overturn an approval that was already granted — the card goes back
   *  to "odrzucona" so the employee can file a corrected one. The original
   *  approval is preserved server-side; the employee AND the original approver
   *  are notified. When the month's payouts are already settled the server answers
   *  409 until `confirmPayrollSettled` is passed. */
  revokeApproval: (id: string, comment: string, confirmPayrollSettled = false) =>
    apiClient
      .post<{ data: Timesheet }>(`/timesheets/${id}/revoke-approval/`, {
        comment,
        ...(confirmPayrollSettled ? { confirm_payroll_settled: true } : {}),
      })
      .then((r) => r.data.data),

  delete: (id: string) =>
    apiClient.delete(`/timesheets/${id}/`),

  pdfPreview: (
    year: number,
    month: number,
    secondaryContractId?: string | null,
    employeeId?: string | null,
  ) =>
    apiClient
      .get<{ data: PdfPreviewResponse }>(
        "/timesheets/pdf_preview/",
        {
          params: {
            year,
            month,
            ...(secondaryContractId ? { secondary_contract: secondaryContractId } : {}),
            ...(employeeId ? { employee: employeeId } : {}),
          },
        },
      )
      .then((r) => r.data.data),

  exportPdf: (payload: {
    year: number; month: number; days: unknown[];
    secondary_contract?: string | null;
    declared_hours?: string | null;
    employee?: string | null;
  }) =>
    apiClient
      .post("/timesheets/export_pdf/", payload, { responseType: "blob" })
      .then((r) => r.data as Blob),

  exportBlankPdf: (params: {
    year: number;
    month: number;
    secondary_contract?: string | null;
    employee?: string | null;
  }) =>
    apiClient
      .get("/timesheets/export_blank_pdf/", {
        params: {
          year: params.year,
          month: params.month,
          ...(params.secondary_contract ? { secondary_contract: params.secondary_contract } : {}),
          ...(params.employee ? { employee: params.employee } : {}),
        },
        responseType: "blob",
      })
      .then((r) => r.data as Blob),

  bulkExportPdf: (payload: {
    year: number;
    month: number;
    primary?: { days: unknown[] } | null;
    secondary?: {
      contract_id: string;
      days: unknown[];
      declared_hours?: string | null;
    } | null;
    employee?: string | null;
  }) =>
    apiClient
      .post("/timesheets/bulk_export_pdf/", payload, { responseType: "blob" })
      .then((r) => r.data as Blob),

  getPdfDraft: (
    year: number,
    month: number,
    secondaryContractId?: string | null,
    employeeId?: string | null,
  ) =>
    apiClient
      .get<{
        data: {
          default_shift: number | null;
          day_states: Record<string, unknown>;
          day_updated_at: Record<string, string>;
          declared_hours: string | null;
          secondary_contract: string | null;
          updated_at?: string;
        } | null;
      }>("/timesheets/pdf_draft/", {
        params: {
          year,
          month,
          ...(secondaryContractId ? { secondary_contract: secondaryContractId } : {}),
          ...(employeeId ? { employee: employeeId } : {}),
        },
      })
      .then((r) => r.data.data),

  savePdfDraft: (payload: {
    year: number;
    month: number;
    default_shift: number | null;
    day_states: Record<string, unknown>;
    declared_hours?: string | null;
    secondary_contract?: string | null;
    employee?: string | null;
  }) =>
    apiClient
      .post("/timesheets/save_pdf_draft/", payload)
      .then(() => undefined),

  bulkSavePdfDraft: (payload: {
    year: number;
    month: number;
    primary?: { default_shift: number | null; day_states: Record<string, unknown> } | null;
    secondary?: {
      contract_id: string;
      default_shift?: number | null;
      day_states?: Record<string, unknown>;
      declared_hours?: string | null;
    } | null;
    employee?: string | null;
  }) =>
    apiClient
      .post("/timesheets/bulk_save_pdf_draft/", payload)
      .then(() => undefined),

  mySecondaryContracts: (year: number, month: number, employeeId?: string | null) =>
    apiClient
      .get<{
        data: SecondaryContractBrief[];
        meta: { count: number; primary: PrimaryContractBrief };
      }>(
        "/timesheets/my_secondary_contracts/",
        { params: { year, month, ...(employeeId ? { employee: employeeId } : {}) } },
      )
      .then((r) => r.data.data),

  // Same endpoint but returns the full envelope so callers can read `meta.primary`.
  mySecondaryContractsWithPrimary: (year: number, month: number, employeeId?: string | null) =>
    apiClient
      .get<{
        data: SecondaryContractBrief[];
        meta: {
          count: number;
          primary: PrimaryContractBrief;
          existing_timesheets?: ExistingTimesheet[];
        };
      }>(
        "/timesheets/my_secondary_contracts/",
        { params: { year, month, ...(employeeId ? { employee: employeeId } : {}) } },
      )
      .then((r) => ({
        secondaries: r.data.data,
        primary: r.data.meta.primary,
        existing: r.data.meta.existing_timesheets ?? [],
      } as MySecondaryContractsResponse)),

  proxyTargets: () =>
    apiClient
      .get<{ data: ProxyTarget[]; meta: { count: number } }>("/timesheets/proxy_targets/")
      .then((r) => r.data.data),

  /** Reviewer-only: how many employees submitted a timesheet for the period.
   *  Branch-scoped (director sees own branch; HR/accountant/CEO see all). */
  submissionStats: (year: number, month: number) =>
    apiClient
      .get<{ data: { submitted: number; total: number } }>(
        "/timesheets/submission-stats/",
        { params: { year, month } },
      )
      .then((r) => r.data.data),

  /** Reviewer-only: list of employees with a saved PDF draft for the period.
   *  Branch-scoped (director sees own branch; HR/accountant/CEO see all). */
  draftsOverview: (year: number, month: number) =>
    apiClient
      .get<{ data: DraftOverviewRow[]; meta: { count: number } }>(
        "/timesheets/drafts-overview/",
        { params: { year, month } },
      )
      .then((r) => r.data.data),

  /** Reviewer-only: employees without a submitted (pending/approved) primary timesheet.
   *  Branch-scoped. Excludes contractors, CEO, B2B. */
  missing: (year: number, month: number) =>
    apiClient
      .get<{ data: TimesheetMissingRow[]; meta: { count: number; year: number; month: number } }>(
        "/timesheets/missing/",
        { params: { year, month } },
      )
      .then((r) => r.data.data),

  /** Append-only audit log for a (year, month, contract) draft.
   *  Employees can read their own (no `employeeId`); reviewers read others'. */
  draftHistory: (
    year: number,
    month: number,
    secondaryContractId?: string | null,
    employeeId?: string | null,
  ) =>
    apiClient
      .get<{ data: DraftHistoryEntry[]; meta: { count: number } }>(
        "/timesheets/draft-history/",
        {
          params: {
            year,
            month,
            ...(secondaryContractId ? { secondary_contract: secondaryContractId } : {}),
            ...(employeeId ? { employee: employeeId } : {}),
          },
        },
      )
      .then((r) => r.data.data),

  /** Reviewer-only: list of dismissed missing-timesheet alerts for a period. */
  getMissingDismissals: (year: number, month: number) =>
    apiClient
      .get<{ data: { employee_id: string; dismissed_by_name: string; dismissed_at: string }[] }>(
        "/timesheets/missing-dismissals/",
        { params: { year, month } },
      )
      .then((r) => r.data.data),

  /** Reviewer-only: dismiss the missing-timesheet alert for an employee. */
  dismissMissing: (employee_id: string, year: number, month: number) =>
    apiClient
      .post("/timesheets/dismiss-missing/", { employee_id, year, month })
      .then((r) => r.data),

  /** Reviewer-only: remove a previously dismissed alert. */
  undismissMissing: (employee_id: string, year: number, month: number) =>
    apiClient
      .delete("/timesheets/dismiss-missing/", { params: { employee_id, year, month } })
      .then((r) => r.data),

  /** Missing-timesheet system lock for the current user (previous month's card overdue). */
  lockStatus: () =>
    apiClient
      .get<{ data: TimesheetLockStatus }>("/timesheets/lock-status/")
      .then((r) => r.data.data),

  /** Check whether the current user's missing-timesheet alert for a period was dismissed by an admin. */
  getMyMissingDismissal: (year: number, month: number) =>
    apiClient
      .get<{ data: { is_dismissed: boolean } }>(
        "/timesheets/my-missing-dismissal/",
        { params: { year, month } },
      )
      .then((r) => r.data.data.is_dismissed),
};
